import ObexRequestsHandlerP80 from '../handlers/ObexRequestsHandlerP80';
import { Warehouse } from '../types/Warehouse.types';
import { MOCK_WAREHOUSES } from '../mocks/Warehouses.mocks';
import { getAccessToken, getActiveCompanyId, buildQuery, unwrapList, unwrapRecord, unwrapError, getTransportError } from './apiUtils';

const ROUTES = {
  list:   '/warehouses_list',
  create: '/warehouses_create',
  update: '/warehouses_update'
};

/**
 * La tabla `warehouses` del backend NO guarda capacity, used, activeShipments,
 * address ni description: solo company_id, name, type, management_model,
 * parent_warehouse_id y status. Esos campos son de la maqueta y se rellenan con
 * valores neutros, así que los indicadores de ocupación de WarehousesView
 * saldrán a cero con datos reales hasta que el backend los contemple.
 */
export const mapWarehouse = (raw: any): Warehouse => ({
  id:                        String(raw.id),
  name:                      raw.name || '',
  societyId:                 raw.company_id !== undefined && raw.company_id !== null ? String(raw.company_id) : '',
  type:                      raw.type === 'virtual' ? 'virtual' : 'physical',
  managementModel:           raw.management_model || 'location',
  capacity:                  isNaN(Number(raw.capacity)) ? 0 : Number(raw.capacity),
  used:                      isNaN(Number(raw.used)) ? 0 : Number(raw.used),
  activeShipments:           isNaN(Number(raw.active_shipments)) ? 0 : Number(raw.active_shipments),
  parentPhysicalWarehouseId: raw.parent_warehouse_id !== undefined && raw.parent_warehouse_id !== null
    ? String(raw.parent_warehouse_id)
    : undefined,
  address:                   raw.address || '',
  description:               raw.description || ''
});

/**
 * Los endpoints de almacenes existen en el repo del backend pero todavía no
 * están publicados en la pasarela. Mientras devuelvan error, el listado cae a
 * MOCK_WAREHOUSES para no dejar la vista vacía (y solo con error, no con un listado vacío); las escrituras sí propagan el
 * fallo, porque fingir que se guardó algo sería peor que el error.
 */
export const listWarehouses = async (
  options: { companyId?: string; allCompanies?: boolean; status?: string; search?: string; page?: number; pageSize?: number } = {}
): Promise<Warehouse[]> => {
  const token = getAccessToken();
  if (!token) return MOCK_WAREHOUSES;

  // Por defecto, solo los almacenes de la sociedad activa. Sin sociedad activa
  // (aún cargando) no se filtra, como antes.
  let companyId = options.companyId;
  if (!companyId && !options.allCompanies) {
    try { companyId = getActiveCompanyId(); } catch { companyId = undefined; }
  }

  const query = buildQuery({
    access_token: token,
    company_id:   companyId,
    status:       options.status,
    search:       options.search,
    page:         options.page || 1,
    page_size:    options.pageSize || 200
  });

  try {
    const res = await ObexRequestsHandlerP80.get(`${ROUTES.list}${query}`);
    // Un listado vacío es una respuesta válida (una sociedad puede no tener almacenes);
    // la maqueta solo entra si el endpoint no responde.
    if (getTransportError(res)) return MOCK_WAREHOUSES;
    return unwrapList(res).map(mapWarehouse);
  } catch (err) {
    console.warn('warehouses_list no disponible, usando datos de maqueta:', err);
    return MOCK_WAREHOUSES;
  }
};

export const createWarehouse = async (warehouse: Omit<Warehouse, 'id'>): Promise<Warehouse> => {
  const token = getAccessToken();
  if (!token) throw new Error('Sesión no válida o expirada.');

  const query = buildQuery({
    access_token:        token,
    company_id:          warehouse.societyId,
    name:                warehouse.name,
    type:                warehouse.type,
    management_model:    warehouse.managementModel,
    parent_warehouse_id: warehouse.parentPhysicalWarehouseId,
    status:              'active'
  });

  const res = await ObexRequestsHandlerP80.get(`${ROUTES.create}${query}`);
  const record = unwrapRecord(res);
  if (!record) throw new Error(unwrapError(res, 'Error al dar de alta el almacén.'));
  return mapWarehouse(record);
};

// warehouses/update.avap no acepta company_id: la sociedad es inmutable.
export const updateWarehouse = async (id: string, warehouse: Partial<Warehouse>): Promise<Warehouse> => {
  const token = getAccessToken();
  if (!token) throw new Error('Sesión no válida o expirada.');

  const query = buildQuery({
    access_token:        token,
    id,
    name:                warehouse.name,
    type:                warehouse.type,
    management_model:    warehouse.managementModel,
    parent_warehouse_id: warehouse.parentPhysicalWarehouseId,
    status:              'active'
  });

  const res = await ObexRequestsHandlerP80.get(`${ROUTES.update}${query}`);
  const record = unwrapRecord(res);
  if (!record) throw new Error(unwrapError(res, 'Error al modificar el almacén.'));
  return mapWarehouse(record);
};
